import React from 'react';

interface WordleGridProps {
  wordLength: number;
  attempts: Array<{
    attemptNumber: number;
    guess: string;
    feedback: string[];
  }>;
  currentGuess: string;
  maxAttempts: number;
  isAnimating?: boolean;
  animatingCellIndex?: number;
  animatingFeedback?: string[] | null;
  selectedCellIndex?: number;
  onCellClick?: (index: number) => void;
  opponentCurrentGuess?: string;
}

const WordleGrid: React.FC<WordleGridProps> = ({
  wordLength,
  attempts,
  currentGuess,
  maxAttempts,
  isAnimating = false,
  animatingCellIndex = -1,
  animatingFeedback = null,
  selectedCellIndex = -1,
  onCellClick,
  opponentCurrentGuess
}) => {
  const getCellColor = (feedback: string) => {
    switch (feedback) {
      case 'green':
        return 'bg-green-500';
      case 'yellow':
        return 'bg-yellow-500';
      case 'gray':
        return 'bg-gray-400';
      default:
        return 'bg-gray-200 border-2 border-gray-300';
    }
  };

  const getTextColor = (feedback: string) => {
    return feedback ? 'text-white font-bold' : 'text-gray-800';
  };

  const renderRow = (rowIndex: number, guess?: string, feedback?: string[], isCurrentRow: boolean = false, isOpponentRow: boolean = false) => {
    const cells = [];
    for (let i = 0; i < wordLength; i++) {
      // Get letter, but ignore spaces (spaces are used as placeholders for empty positions)
      const letter = guess && guess[i] && guess[i] !== ' ' ? guess[i] : '';
      // Use animatingFeedback if available and we're animating, otherwise use regular feedback
      const cellFeedback = isCurrentRow && isAnimating && animatingFeedback 
        ? (i <= animatingCellIndex ? animatingFeedback[i] : '')
        : (feedback ? feedback[i] : '');
      const isAnimatingCell = isCurrentRow && isAnimating && i === animatingCellIndex;
      const hasAnimated = isCurrentRow && isAnimating && i < animatingCellIndex;
      // Only show selection on current player's row, not opponent's row
      const isSelected = isCurrentRow && !isOpponentRow && !cellFeedback && i === selectedCellIndex;
      
      // Animation class - show actual feedback color during animation
      let animationClass = '';
      if (isCurrentRow && isAnimating) {
        if (isAnimatingCell) {
          // Currently animating cell - ring effect with the actual color (no scale to prevent layout shift)
          animationClass = 'ring-4 ring-opacity-75 shadow-lg';
          if (cellFeedback === 'green') {
            animationClass += ' ring-green-400';
          } else if (cellFeedback === 'yellow') {
            animationClass += ' ring-yellow-400';
          } else if (cellFeedback === 'gray') {
            animationClass += ' ring-gray-400';
          } else {
            animationClass += ' ring-blue-400';
          }
        }
        // Remove scale-105 from already animated cells to prevent layout issues
      }
      
      // Selection border
      const selectionClass = isSelected 
        ? 'ring-4 ring-blue-500 ring-offset-2 cursor-pointer' 
        : isCurrentRow && !cellFeedback && onCellClick
          ? 'cursor-pointer hover:ring-2 hover:ring-blue-300'
          : '';
      
      cells.push(
        <div
          key={i}
          onClick={() => isCurrentRow && !isOpponentRow && !cellFeedback && onCellClick && onCellClick(i)}
          className={`w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center rounded-lg ${getCellColor(cellFeedback)} ${getTextColor(cellFeedback)} text-xl sm:text-2xl font-semibold transition-all duration-200 ease-out ${animationClass} ${selectionClass}`}
          style={{
            // Ensure no transform scale that could cause layout shift
            transform: 'none',
            willChange: isAnimatingCell ? 'transform, background-color' : 'auto',
            backfaceVisibility: 'hidden',
            perspective: '1000px',
            transformStyle: 'preserve-3d'
          }}
        >
          {letter}
        </div>
      );
    }
    return cells;
  };

  const allRows = [];
  
  // Determine which row is animating (the last completed attempt, not the current guess row)
  const animatingRowIndex = isAnimating && attempts.length > 0 ? attempts.length - 1 : -1;
  
  // Always render exactly maxAttempts rows
  for (let rowIndex = 0; rowIndex < maxAttempts; rowIndex++) {
    if (rowIndex < attempts.length) {
      // Render completed attempt
      // This row is animating if it's the last attempt and animation is active
      const isAnimatingRow = rowIndex === animatingRowIndex;
      allRows.push(
        <div key={`attempt-${rowIndex}`} className="flex gap-2 justify-center">
          {renderRow(rowIndex, attempts[rowIndex].guess, attempts[rowIndex].feedback, isAnimatingRow)}
        </div>
      );
    } else if (rowIndex === attempts.length) {
      // Render current guess row
      // If opponentCurrentGuess is provided and has content, show it in this row (first row for observing)
      // Otherwise show currentGuess if it has content
      const showOpponentGuess = !!(opponentCurrentGuess && opponentCurrentGuess.trim().length > 0);
      const guessToShow = showOpponentGuess ? opponentCurrentGuess : currentGuess;
      const hasContent = guessToShow && guessToShow.trim().length > 0;
      // Mark as current row if it's the active player's turn (not opponent), or if showing opponent guess
      // Always mark as current row if it's not opponent's turn and not animating, even if empty (for selection display)
      // For player's own row, always mark as current if not animating, so selection is visible even when empty
      const isCurrentRow: boolean = !isAnimating && (showOpponentGuess ? !!hasContent : true);
      const isOpponentRow: boolean = showOpponentGuess;
      // Always render this row if it's the current guess row, even if empty (for opponent typing)
      allRows.push(
        <div key="current" className="flex gap-2 justify-center">
          {renderRow(rowIndex, guessToShow || '', undefined, isCurrentRow, isOpponentRow)}
        </div>
      );
    } else {
      // Render empty row
      allRows.push(
        <div key={`empty-${rowIndex}`} className="flex gap-2 justify-center">
          {renderRow(rowIndex, '', undefined)}
        </div>
      );
    }
  }

  return (
    <div className="flex flex-col gap-2" style={{ isolation: 'isolate' }}>
      {allRows}
    </div>
  );
};

export default WordleGrid;

